import React from 'react'



/**

 * Read-only display of linked PQR No.(s), weld ID(s), WPS No., and Rev.

 * in the WPS Preview page header.

 */

export interface WpsPreviewHeaderPillsProps {

  /** One pill per linked PQR number (from selected PQR trains). */

  pqrNumbers?: string[]

  /** One pill per scoped weld id (union from selected PQR trains). */

  weldIds?: string[]

  wpsNumber: string

  revision: string

  /** Legacy bevel-scoped rows when weldIds are absent. */

  bevelLabel?: string

}



const EM_DASH = '\u2014'



type PillTone = 'weld' | 'pqr' | 'wps' | 'rev' | 'bevel'



const TONE_CLASSES: Record<PillTone, string> = {

  weld: 'bg-blueprint-blue/10 text-blueprint-blue border border-blueprint-blue/20',

  pqr: 'bg-galvanizing-green/10 text-galvanizing-green border border-galvanizing-green/20',

  wps: 'bg-galvanizing-green/10 text-galvanizing-green border border-galvanizing-green/20',

  rev: 'bg-amber-500/10 text-amber-700 border border-amber-500/30',

  bevel: 'bg-blueprint-blue/10 text-blueprint-blue border border-blueprint-blue/20'

}



function Pill({

  tone,

  testId,

  label,

  value

}: {

  tone: PillTone

  testId: string

  label?: string

  value: string

}) {

  return (

    <span

      data-testid={testId}

      className={`inline-flex items-center gap-1.5 rounded-full px-1.5 py-0.5 text-[9px] font-medium ${TONE_CLASSES[tone]}`}

    >

      {label ? (

        <span className="font-semibold uppercase tracking-wide">{label}:</span>

      ) : null}

      <span>{value}</span>

    </span>

  )

}



export default function WpsPreviewHeaderPills({

  pqrNumbers,

  weldIds,

  wpsNumber,

  revision,

  bevelLabel

}: WpsPreviewHeaderPillsProps) {

  const pqrs = (pqrNumbers ?? []).map((s) => s.trim()).filter(Boolean)

  const welds = (weldIds ?? []).map((s) => s.trim()).filter(Boolean)

  const showLegacyBevel = welds.length === 0 && (bevelLabel || '').trim().length > 0



  return (

    <div

      data-testid="wps-preview-header-pills"

      className="flex flex-wrap items-center gap-1.5"

    >

      {pqrs.length > 0

        ? pqrs.map((pn, idx) => (

            <Pill

              key={pn}

              tone="pqr"

              testId={idx === 0 ? 'wps-pill-pqr-number' : `wps-pill-pqr-number-${idx}`}

              value={pn}

            />

          ))

        : null}

      {welds.length > 0

        ? welds.map((wid, idx) => (

            <Pill

              key={wid}

              tone="weld"

              testId={idx === 0 ? 'wps-pill-weld-id' : `wps-pill-weld-id-${idx}`}

              value={wid}

            />

          ))

        : showLegacyBevel ? (

            <Pill tone="bevel" testId="wps-pill-bevel" value={bevelLabel!.trim()} />

          ) : null}

      <Pill

        tone="wps"

        testId="wps-pill-wps-number"

        value={wpsNumber.trim() ? wpsNumber : EM_DASH}

      />

      <Pill

        tone="rev"

        testId="wps-pill-rev"

        label="Rev"

        value={revision.trim() ? revision : EM_DASH}

      />

    </div>

  )

}

